import { Paperclip, Trash2 } from 'lucide-react';
import { type FormEvent, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { AuditStandard } from '@repo/contracts';
import {
  AUDIT_STANDARD_LABELS,
  AUDIT_STANDARDS,
} from '@repo/contracts/services';
import {
  formatMoney,
  MAX_SELF_SERVE_PAGES,
  priceFor,
  REPORT_TURNAROUND_LABEL,
} from '@repo/contracts/services-pricing';

import { attributionForRequest } from '../analytics/attribution';
import { trackLeadConversion } from '../analytics/google-ads';
import { analyticsId, trackEvent } from '../analytics/posthog';
import { ApiError } from '../api/client';
import { useSubmitServiceRequest } from '../api/services';
import { describedBy, Field, inputClass } from '../components/field';
import { buttonClass, Card, Container, Eyebrow } from '../components/ui';
import { CONTACT_EMAIL } from '../content/site';
import {
  ALLOWED_EXTENSIONS,
  type FormErrors,
  MAX_FILES,
  normalizeUrl,
  type RequestForm,
  splitUrls,
  validate,
} from './request-validation';

const ERROR_ORDER: (keyof FormErrors)[] = [
  'name',
  'email',
  'company',
  'url',
  'additionalUrls',
  'estimatedPages',
  'deadline',
  'files',
];

const FIELD_LABELS: Record<keyof FormErrors, string> = {
  additionalUrls: 'Other pages or flows',
  company: 'Company',
  deadline: 'Needed by',
  email: 'Work email',
  estimatedPages: 'Estimated pages',
  files: 'Documents',
  name: 'Your name',
  notes: 'Anything else',
  phone: 'Phone',
  requiresLogin: 'Login',
  role: 'Role',
  standard: 'Standard',
  url: 'Website address',
  website: 'Website',
};

function initialPages(param: string | null): string {
  const n = Number(param);
  return Number.isInteger(n) && n >= 1 && n <= MAX_SELF_SERVE_PAGES
    ? String(n)
    : '10';
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RequestPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const submit = useSubmitServiceRequest();
  const summaryRef = useRef<HTMLDivElement>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<RequestForm>({
    additionalUrls: '',
    company: '',
    deadline: '',
    email: '',
    estimatedPages: initialPages(params.get('pages')),
    name: '',
    notes: '',
    phone: '',
    requiresLogin: false,
    role: '',
    standard: 'wcag22-aa',
    url: '',
    website: '',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const started = useRef(false);

  const set = <K extends keyof RequestForm>(key: K, value: RequestForm[K]) => {
    if (!started.current) {
      started.current = true;
      // Came from the pricing calculator, or straight to the form.
      trackEvent('request_started', { from_pricing: params.has('pages') });
    }
    setForm((f) => ({ ...f, [key]: value }));
    // Once the person has tried to submit, keep errors live as they fix them.
    if (submitted) {
      setErrors(validate({ ...form, [key]: value }, files));
    }
  };

  const pages = Number(form.estimatedPages);
  const quote =
    Number.isInteger(pages) && pages >= 1 && pages <= MAX_SELF_SERVE_PAGES
      ? priceFor(pages)
      : null;

  const errorList = ERROR_ORDER.filter((k) => errors[k]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (submit.isPending) return;
    setSubmitted(true);
    const found = validate(form, files);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      // Which fields, never what was in them.
      trackEvent('request_invalid', {
        fields: ERROR_ORDER.filter((k) => found[k]).join(','),
      });
      // GOV.UK pattern: move focus to the error summary, which links to each field.
      requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    const visitor = analyticsId();
    const attribution = attributionForRequest();
    const result = await submit
      .mutateAsync({
        files,
        input: {
          contact: {
            company: form.company.trim(),
            email: form.email.trim(),
            name: form.name.trim(),
            ...(form.phone.trim() ? { phone: form.phone.trim() } : {}),
            ...(form.role.trim() ? { role: form.role.trim() } : {}),
          },
          site: {
            additionalUrls: splitUrls(form.additionalUrls).map(
              (u) => normalizeUrl(u) as string,
            ),
            estimatedPages: pages,
            requiresLogin: form.requiresLogin,
            standard: form.standard as AuditStandard,
            url: normalizeUrl(form.url) as string,
            ...(form.deadline ? { deadline: form.deadline } : {}),
            ...(form.notes.trim() ? { notes: form.notes.trim() } : {}),
          },
          ...(form.website ? { website: form.website } : {}),
          ...(visitor ? { analyticsId: visitor } : {}),
          ...(attribution ? { attribution } : {}),
        },
      })
      .catch((err: unknown) => {
        trackEvent('request_failed', {
          status: err instanceof ApiError ? err.status : 0,
        });
        return null;
      });

    if (result) {
      // Here, on the success path — never on `/r/:token`, which people revisit.
      trackLeadConversion(result.reference);
      void navigate(`/r/${encodeURIComponent(result.statusToken)}?new=1`, {
        state: { failedUploads: result.failedUploads },
      });
    } else {
      requestAnimationFrame(() => summaryRef.current?.focus());
    }
  }

  function addFiles(list: FileList | null) {
    if (!list) return;
    const next = [...files, ...Array.from(list)];
    setFiles(next);
    if (submitted) setErrors(validate(form, next));
    if (fileInput.current) fileInput.current.value = '';
  }

  function removeFile(index: number) {
    const next = files.filter((_, i) => i !== index);
    setFiles(next);
    if (submitted) setErrors(validate(form, next));
  }

  const aria = (key: keyof FormErrors, hint?: boolean) => ({
    'aria-describedby': describedBy(key, {
      error: errors[key],
      hint: hint ? 'yes' : undefined,
    }),
    'aria-invalid': errors[key] ? true : undefined,
    id: key,
  });

  return (
    <Container className="py-14 md:py-20">
      <div className="max-w-2xl">
        <Eyebrow>Request an audit</Eyebrow>
        <h1 className="mt-3 font-report text-report-title font-medium text-text-primary">
          Tell us about your site
        </h1>
        <p className="mt-4 text-report-lede text-text-secondary">
          It takes about three minutes. Requesting is free — we&rsquo;ll be in
          touch within one business day to confirm the scope.
        </p>
      </div>

      <div className="mt-12 grid gap-10 lg:grid-cols-12">
        <form
          className="space-y-10 lg:col-span-8"
          noValidate
          onSubmit={(e) => void onSubmit(e)}
        >
          {errorList.length > 0 || submit.isError ? (
            <div
              aria-labelledby="error-summary-title"
              className="rounded-card border-2 border-brand-strong bg-surface p-6"
              id="error-summary"
              ref={summaryRef}
              role="alert"
              tabIndex={-1}
            >
              <h2
                className="text-subtitle font-semibold text-text-primary"
                id="error-summary-title"
              >
                {submit.isError
                  ? 'We couldn’t send your request'
                  : 'There’s a problem'}
              </h2>
              {submit.isError ? (
                <p className="mt-2 text-ui-lg text-text-primary">
                  {submit.error.message} If it keeps happening, email{' '}
                  <a className="underline" href={`mailto:${CONTACT_EMAIL}`}>
                    {CONTACT_EMAIL}
                  </a>
                  .
                </p>
              ) : (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-ui-lg">
                  {errorList.map((key) => (
                    <li key={key}>
                      <a
                        className="font-medium text-brand-strong underline"
                        href={`#${key}`}
                      >
                        {FIELD_LABELS[key]}: {errors[key]}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}

          <fieldset>
            <legend className="text-card-title text-text-primary">
              About you
            </legend>
            <div className="mt-6 grid gap-6 sm:grid-cols-2">
              <Field error={errors.name} id="name" label="Your name">
                <input
                  autoComplete="name"
                  className={inputClass(errors.name, 'h-12')}
                  onChange={(e) => set('name', e.target.value)}
                  type="text"
                  value={form.name}
                  {...aria('name')}
                />
              </Field>
              <Field error={errors.email} id="email" label="Work email">
                <input
                  autoComplete="email"
                  className={inputClass(errors.email, 'h-12')}
                  onChange={(e) => set('email', e.target.value)}
                  type="email"
                  value={form.email}
                  {...aria('email')}
                />
              </Field>
              <Field error={errors.company} id="company" label="Company">
                <input
                  autoComplete="organization"
                  className={inputClass(errors.company, 'h-12')}
                  onChange={(e) => set('company', e.target.value)}
                  type="text"
                  value={form.company}
                  {...aria('company')}
                />
              </Field>
              <Field id="role" label="Role" optional>
                <input
                  autoComplete="organization-title"
                  className={inputClass(undefined, 'h-12')}
                  id="role"
                  onChange={(e) => set('role', e.target.value)}
                  type="text"
                  value={form.role}
                />
              </Field>
              <Field id="phone" label="Phone" optional>
                <input
                  autoComplete="tel"
                  className={inputClass(undefined, 'h-12')}
                  id="phone"
                  onChange={(e) => set('phone', e.target.value)}
                  type="tel"
                  value={form.phone}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-card-title text-text-primary">
              Your site
            </legend>
            <div className="mt-6 space-y-6">
              <Field
                error={errors.url}
                hint="A website or web app, e.g. https://www.example.com. Native mobile and desktop apps aren’t supported yet."
                id="url"
                label="Website address"
              >
                <input
                  autoComplete="url"
                  className={inputClass(errors.url, 'h-12')}
                  inputMode="url"
                  onChange={(e) => set('url', e.target.value)}
                  type="text"
                  value={form.url}
                  {...aria('url', true)}
                />
              </Field>
              <Field
                error={errors.additionalUrls}
                hint="Specific pages or journeys that matter most, one per line — e.g. sign-up, checkout, account settings."
                id="additionalUrls"
                label="Other pages or flows"
                optional
              >
                <textarea
                  className={inputClass(errors.additionalUrls, 'min-h-28 py-3')}
                  onChange={(e) => set('additionalUrls', e.target.value)}
                  value={form.additionalUrls}
                  {...aria('additionalUrls', true)}
                />
              </Field>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field
                  error={errors.estimatedPages}
                  hint="Distinct templates and key screens. We confirm the final count with you."
                  id="estimatedPages"
                  label="Estimated pages"
                >
                  <input
                    className={inputClass(errors.estimatedPages, 'h-12')}
                    inputMode="numeric"
                    max={MAX_SELF_SERVE_PAGES}
                    min={1}
                    onChange={(e) => set('estimatedPages', e.target.value)}
                    type="number"
                    value={form.estimatedPages}
                    {...aria('estimatedPages', true)}
                  />
                </Field>
                <Field
                  hint="Most contracts and regulations reference WCAG 2.1 or 2.2 AA."
                  id="standard"
                  label="Standard"
                >
                  <select
                    aria-describedby="standard-hint"
                    className={inputClass(undefined, 'h-12')}
                    id="standard"
                    onChange={(e) => set('standard', e.target.value)}
                    value={form.standard}
                  >
                    {AUDIT_STANDARDS.map((s) => (
                      <option key={s} value={s}>
                        {AUDIT_STANDARD_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="rounded-card border border-hairline bg-surface p-5">
                <div className="flex items-start gap-3">
                  <input
                    aria-describedby="requiresLogin-hint"
                    checked={form.requiresLogin}
                    className="mt-1 size-5 shrink-0 cursor-pointer accent-brand-strong"
                    id="requiresLogin"
                    onChange={(e) => set('requiresLogin', e.target.checked)}
                    type="checkbox"
                  />
                  <div>
                    <label
                      className="text-ui-strong font-semibold text-text-primary"
                      htmlFor="requiresLogin"
                    >
                      Some pages are behind a login
                    </label>
                    <p
                      className="mt-1 text-ui text-text-secondary"
                      id="requiresLogin-hint"
                    >
                      We&rsquo;ll arrange access during scoping — please
                      don&rsquo;t send passwords or credentials here.
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid gap-6 sm:grid-cols-2">
                <Field
                  error={errors.deadline}
                  id="deadline"
                  label="Needed by"
                  optional
                >
                  <input
                    className={inputClass(errors.deadline, 'h-12')}
                    onChange={(e) => set('deadline', e.target.value)}
                    type="date"
                    value={form.deadline}
                    {...aria('deadline')}
                  />
                </Field>
              </div>
              <Field
                hint="Context that helps us: what triggered the audit, known issues, a procurement or legal deadline."
                id="notes"
                label="Anything else"
                optional
              >
                <textarea
                  aria-describedby="notes-hint"
                  className={inputClass(undefined, 'min-h-32 py-3')}
                  id="notes"
                  onChange={(e) => set('notes', e.target.value)}
                  value={form.notes}
                />
              </Field>
            </div>
          </fieldset>

          <fieldset>
            <legend className="text-card-title text-text-primary">
              Documents
            </legend>
            <p className="mt-2 text-ui-lg text-text-secondary" id="files-hint">
              Optional: design files, a previous audit or VPAT, a test plan. Up
              to {MAX_FILES} files, 20&nbsp;MB each (
              {ALLOWED_EXTENSIONS.join(', ')}).
            </p>
            <div className="mt-4">
              <label
                className={`${buttonClass('secondary')} cursor-pointer focus-within:outline-2 focus-within:outline-offset-3 focus-within:outline-brand-strong`}
                htmlFor="files"
              >
                <Paperclip aria-hidden size={18} /> Attach files
                <input
                  accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
                  aria-describedby={describedBy('files', {
                    error: errors.files,
                    hint: 'yes',
                  })}
                  aria-invalid={errors.files ? true : undefined}
                  className="sr-only"
                  id="files"
                  multiple
                  onChange={(e) => addFiles(e.target.files)}
                  ref={fileInput}
                  type="file"
                />
              </label>
              {errors.files ? (
                <p
                  className="mt-2 text-ui font-medium text-brand-strong"
                  id="files-error"
                >
                  <span className="sr-only">Error:</span> {errors.files}
                </p>
              ) : null}
              {files.length > 0 ? (
                <ul
                  aria-label="Attached files"
                  className="mt-4 divide-y divide-hairline rounded-card border border-hairline bg-surface"
                >
                  {files.map((file, i) => (
                    <li
                      className="flex items-center justify-between gap-4 px-4 py-3"
                      key={`${file.name}-${i}`}
                    >
                      <span className="min-w-0 truncate text-ui-lg text-text-primary">
                        {file.name}
                      </span>
                      <span className="flex shrink-0 items-center gap-3">
                        <span className="text-ui text-text-secondary">
                          {formatBytes(file.size)}
                        </span>
                        <button
                          aria-label={`Remove ${file.name}`}
                          className="inline-flex size-10 items-center justify-center rounded-button text-text-secondary hover:bg-surface-hover hover:text-text-primary"
                          onClick={() => removeFile(i)}
                          type="button"
                        >
                          <Trash2 aria-hidden size={18} />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          </fieldset>

          {/* Honeypot: hidden from people and assistive tech, filled by bots. */}
          <div
            aria-hidden
            className="absolute -left-[9999px] h-px w-px overflow-hidden"
          >
            <label htmlFor="website">Leave this empty</label>
            <input
              autoComplete="off"
              id="website"
              onChange={(e) => set('website', e.target.value)}
              tabIndex={-1}
              type="text"
              value={form.website}
            />
          </div>

          <div className="border-t border-hairline pt-8">
            <button
              aria-disabled={submit.isPending}
              className={`${buttonClass('primary')} w-full sm:w-auto`}
              type="submit"
            >
              {submit.isPending ? 'Sending your request…' : 'Send request'}
            </button>
            <p className="mt-3 text-ui text-text-secondary">
              Nothing is charged now. You&rsquo;ll get a link to track your
              request.
            </p>
          </div>
        </form>

        <aside aria-labelledby="estimate-title" className="lg:col-span-4">
          <Card className="lg:sticky lg:top-28">
            <h2
              className="text-small-label uppercase text-text-secondary"
              id="estimate-title"
            >
              Your estimate
            </h2>
            <p
              className="mt-2 font-report text-report-stat font-medium tabular-nums text-text-primary"
              data-testid="request-estimate"
            >
              {quote ? formatMoney(quote.total) : '—'}
            </p>
            <p className="mt-1 text-ui-lg text-text-secondary">
              {quote
                ? `${quote.pages} ${quote.pages === 1 ? 'page' : 'pages'} × ${formatMoney(quote.ratePerPage)}${quote.minimumApplied ? ' (minimum order)' : ''}`
                : 'Enter a page count'}
            </p>
            <h3 className="mt-6 border-t border-hairline pt-6 text-ui-strong font-semibold text-text-primary">
              What happens next
            </h3>
            <ol className="mt-3 list-decimal space-y-2 pl-5 text-ui text-text-secondary">
              <li>We confirm pages and access with you.</li>
              <li>
                You get a teaser of our findings, a fixed quote and an invoice.
              </li>
              <li>
                Your full report usually arrives within{' '}
                {REPORT_TURNAROUND_LABEL} of payment, depending on scope.
              </li>
            </ol>
          </Card>
        </aside>
      </div>
    </Container>
  );
}
